<template>
  <section class="cc-action-only">
    <div class="cc-action-list">
      <button class="cc-action-row cc-action-row--blue" @click="openNewApps" :disabled="rowState.newApps.loading">
        <div class="cc-action-icon">
          <i class="fa-solid fa-file-circle-plus"></i>
        </div>
        <div class="cc-action-meta">
          <div class="cc-action-label">
            New Applications
            <i v-if="rowState.newApps.loading" class="fa-solid fa-spinner fa-spin cc-action-inline"></i>
            <i v-else-if="rowState.newApps.error" class="fa-solid fa-triangle-exclamation cc-action-inline cc-action-inline--error"></i>
          </div>
          <div class="cc-action-value">{{ counts.newApps }}</div>
        </div>
        <i class="fa-solid fa-chevron-right cc-action-chev"></i>
      </button>

      <button class="cc-action-row cc-action-row--red" @click="openExpiringLocks" :disabled="rowState.expiringLocks.loading">
        <div class="cc-action-icon">
          <i class="fa-solid fa-clock"></i>
        </div>
        <div class="cc-action-meta">
          <div class="cc-action-label">
            Expiring Locks
            <i v-if="rowState.expiringLocks.loading" class="fa-solid fa-spinner fa-spin cc-action-inline"></i>
            <i v-else-if="rowState.expiringLocks.error" class="fa-solid fa-triangle-exclamation cc-action-inline cc-action-inline--error"></i>
          </div>
          <div class="cc-action-value">{{ counts.expiringLocks }}</div>
        </div>
        <i class="fa-solid fa-chevron-right cc-action-chev"></i>
      </button>

      <button class="cc-action-row cc-action-row--red" @click="openPastDue" :disabled="rowState.pastDue.loading">
        <div class="cc-action-icon">
          <i class="fa-solid fa-calendar-xmark"></i>
        </div>
        <div class="cc-action-meta">
          <div class="cc-action-label">
            Past Due (Tasks)
            <i v-if="rowState.pastDue.loading" class="fa-solid fa-spinner fa-spin cc-action-inline"></i>
            <i v-else-if="rowState.pastDue.error" class="fa-solid fa-triangle-exclamation cc-action-inline cc-action-inline--error"></i>
          </div>
          <div class="cc-action-value">{{ counts.pastDue }}</div>
        </div>
        <i class="fa-solid fa-chevron-right cc-action-chev"></i>
      </button>

      <button class="cc-action-row cc-action-row--amber" @click="openPendingLLTasks" :disabled="rowState.pendingLLTasks.loading">
        <div class="cc-action-icon">
          <i class="fa-solid fa-hourglass-half"></i>
        </div>
        <div class="cc-action-meta">
          <div class="cc-action-label">
            Pending LL Tasks
            <i v-if="rowState.pendingLLTasks.loading" class="fa-solid fa-spinner fa-spin cc-action-inline"></i>
            <i v-else-if="rowState.pendingLLTasks.error" class="fa-solid fa-triangle-exclamation cc-action-inline cc-action-inline--error"></i>
          </div>
          <div class="cc-action-value">{{ counts.pendingLLTasks }}</div>
        </div>
        <i class="fa-solid fa-chevron-right cc-action-chev"></i>
      </button>

      <button class="cc-action-row cc-action-row--amber" @click="openDisclosures" :disabled="rowState.pendingLOSignature.loading">
        <div class="cc-action-icon">
          <i class="fa-solid fa-file-signature"></i>
        </div>
        <div class="cc-action-meta">
          <div class="cc-action-label">
            Pending LO Signature
            <i v-if="rowState.pendingLOSignature.loading" class="fa-solid fa-spinner fa-spin cc-action-inline"></i>
            <i v-else-if="rowState.pendingLOSignature.error" class="fa-solid fa-triangle-exclamation cc-action-inline cc-action-inline--error"></i>
          </div>
          <div class="cc-action-value">{{ counts.pendingLOSignature }}</div>
        </div>
        <i class="fa-solid fa-chevron-right cc-action-chev"></i>
      </button>

      <LoanLaunchChatSummaryCard :unread-total="loanLaunchUnread" @open="openLoanLaunch" />

      <button v-if="hasAnyError" class="cc-action-row cc-action-row--muted" @click="refresh">
        <div class="cc-action-icon">
          <i class="fa-solid fa-rotate-right"></i>
        </div>
        <div class="cc-action-meta">
          <div class="cc-action-label">Refresh</div>
          <div class="cc-action-value">Retry failed loads</div>
        </div>
        <i class="fa-solid fa-chevron-right cc-action-chev"></i>
      </button>
    </div>
  </section>
</template>

<script>
import apiService from "@/api/api.service.js"
import LoanLaunchChatSummaryCard from "./LoanLaunchChatSummaryCard.vue"
import { isPastDue } from "@/helpers/formatters.js"

const makeRow = () => ({ loading: false, error: null })

export default {
  name: "ActionRequiredCard",
  props: {
    azureId: { type: String, required: true },
    accessToken: { type: String, required: true },
    taskSwitchChecked: { type: Boolean, default: false },
    newAppsColumns: { type: Array, required: true },
    expiringLocksColumns: { type: Array, required: true },
    openTaskColumns: { type: Array, required: true },
    internalTaskColumns: { type: Array, required: false },
    loanLaunchUnread: { type: Number, default: 0 }
  },
  emits: ["open-grid", "open-disclosures", "send-reminders", "open-loanlaunch"],
  data() {
    return {
      newAppsData: [],
      expiringLocksUserLoans: [],
      expiringLocksBranchLoans: [],
      pendingLLTasksData: [],
      pendingLOSignatureData: [],
      pastDueData: [],
      rowState: {
        newApps: makeRow(),
        expiringLocks: makeRow(),
        pendingLLTasks: makeRow(),
        pendingLOSignature: makeRow(),
        pastDue: makeRow()
      },
      pastDueCache: {
        user: null,
        org: null
      },
      hasLoadedOnce: false
    }
  },
  computed: {
    expiringLocksActive() {
      return this.taskSwitchChecked ? this.expiringLocksBranchLoans : this.expiringLocksUserLoans
    },
    counts() {
      return {
        newApps: this.rowState.newApps.error ? "-" : this.newAppsData.length,
        expiringLocks: this.rowState.expiringLocks.error ? "-" : this.expiringLocksActive.length,
        pendingLLTasks: this.rowState.pendingLLTasks.error ? "-" : this.pendingLLTasksData.length,
        pendingLOSignature: this.rowState.pendingLOSignature.error ? "-" : this.pendingLOSignatureData.length,
        pastDue: this.rowState.pastDue.error ? "-" : this.pastDueData.length
      }
    },
    hasAnyError() {
      return Object.values(this.rowState).some((r) => !!r.error)
    }
  },
 watch: {
  async taskSwitchChecked() {
    await Promise.all([
      this.fetchPastDueTasks({ allowCache: true }),
      this.fetchPendingLLTasks()
    ])
  }
},
  methods: {
    normalizeDate(v) {
      return v ? new Date(v) : null
    },
    setRowLoading(key, v) {
      this.rowState[key].loading = v
      if (v) this.rowState[key].error = null
    },
    setRowError(key, err) {
      this.rowState[key].error = err?.message || "Failed to load"
    },

    async refresh() {
      await this.fetchAll({ onlyFailed: true })
    },

    async fetchAll({ onlyFailed = false } = {}) {
      const tasks = []

      if (!onlyFailed || this.rowState.newApps.error) tasks.push(this.fetchNewApps())
      if (!onlyFailed || this.rowState.expiringLocks.error) tasks.push(this.fetchExpiringLocks())
      if (!onlyFailed || this.rowState.pendingLLTasks.error) tasks.push(this.fetchPendingLLTasks())
      if (!onlyFailed || this.rowState.pendingLOSignature.error) tasks.push(this.fetchDisclosures())
      if (!onlyFailed || this.rowState.pastDue.error) tasks.push(this.fetchPastDueTasks({ allowCache: false }))

      await Promise.allSettled(tasks)

      this.hasLoadedOnce = true
    },

    async fetchNewApps() {
      const key = "newApps"
      this.setRowLoading(key, true)
      try {
        const res = await apiService.GetSwiftDashboardNewApplications(this.azureId, this.accessToken)
        const data = res?.data?.data ?? []

        this.newAppsData = data.map((t) => ({
          ...t,
          logmsdatestarted: this.normalizeDate(t.logmsdatestarted)
        }))
      } catch (err) {
        this.newAppsData = []
        this.setRowError(key, err)
      } finally {
        this.setRowLoading(key, false)
      }
    },

    async fetchExpiringLocks() {
      const key = "expiringLocks"
      this.setRowLoading(key, true)
      try {
        const res = await apiService.GetExpiringLocks(this.azureId, this.accessToken)
        const data = res?.data?.data ?? {}

        this.expiringLocksBranchLoans = (data.branchloans ?? []).map((t) => ({
          ...t,
          cust17fv: this.normalizeDate(t.cust17fv)
        }))

        this.expiringLocksUserLoans = (data.userloans ?? []).map((t) => ({
          ...t,
          cust17fv: this.normalizeDate(t.cust17fv)
        }))
      } catch (err) {
        this.expiringLocksBranchLoans = []
        this.expiringLocksUserLoans = []
        this.setRowError(key, err)
      } finally {
        this.setRowLoading(key, false)
      }
    },

async fetchPendingLLTasks() {
  const key = "pendingLLTasks"
  this.setRowLoading(key, true)
  try {
    const res = await apiService.GetSwiftDashboardData(
      this.azureId,
      this.taskSwitchChecked,
      this.taskSwitchChecked,
      this.accessToken
    )

    const raw = res?.data?.data?.data ?? res?.data?.data ?? []

    const isEC = (item) => {
      const t = String(item?.title || "").toLowerCase()
      return t.includes("econsent") || t.includes("e-consent")
    }

    this.pendingLLTasksData = raw
      .map((item) => ({
        ...item,
        created: this.normalizeDate(item.created),
        cust17fv: this.normalizeDate(item.cust17fv)
      }))
      .filter((item) => item.status === "Pending" || item.status === "Requested")
      .filter((item) => !isEC(item))
  } catch (err) {
    this.pendingLLTasksData = []
    this.setRowError(key, err)
  } finally {
    this.setRowLoading(key, false)
  }
},
_groupPastDueByLoan(items) {
  const map = new Map()

  const loan = (x) => String(x?.f364 ?? x?.loannumber ?? "").trim()
  const borrower = (x) => String(x?.borrower_name ?? x?.borrowername ?? x?.name ?? "").trim()

  ;(items || []).forEach((t) => {
    const loanNumber = loan(t) || "—"
    const key = loanNumber

    if (!map.has(key)) {
      map.set(key, {
        loanNumber,
        borrowers: new Set(),
        tasks: []
      })
    }

    const g = map.get(key)
    const b = borrower(t)
    if (b && b !== "—") g.borrowers.add(b)
    g.tasks.push(t)
  })

  return Array.from(map.values())
    .map((g) => ({
      loanNumber: g.loanNumber,
      borrowers: Array.from(g.borrowers),
      tasks: g.tasks
    }))
    .sort((a, b) => String(a.loanNumber).localeCompare(String(b.loanNumber)))
},
_groupPendingLLByLoan(items) {
  const map = new Map()

  const getLoanKey = (x) => String(x?.f364 ?? x?.loanid ?? x?.id ?? "")
  const getBorrower = (x) => String(x?.borrower_name ?? x?.borrowername ?? x?.name ?? "").trim()

  items.forEach((t) => {
    const loanKey = getLoanKey(t)
    if (!loanKey) return

    if (!map.has(loanKey)) {
      map.set(loanKey, {
        loanNumber: loanKey,
        borrowers: new Set(),
        tasks: []
      })
    }

    const g = map.get(loanKey)
    const b = getBorrower(t)
    if (b) g.borrowers.add(b)
    g.tasks.push(t)
  })

  return Array.from(map.values()).map((g) => ({
    loanNumber: g.loanNumber,
    borrowers: Array.from(g.borrowers),
    tasks: g.tasks
  }))
},

    async fetchDisclosures() {
      const key = "pendingLOSignature"
      this.setRowLoading(key, true)
      try {
        const res = await apiService.GetLODisclosuresCount(this.azureId, this.accessToken)
        this.pendingLOSignatureData = res?.data?.data?.data ?? []
      } catch (err) {
        this.pendingLOSignatureData = []
        this.setRowError(key, err)
      } finally {
        this.setRowLoading(key, false)
      }
    },

    async fetchPastDueTasks({ allowCache = true } = {}) {
      const key = "pastDue"
      this.setRowLoading(key, true)

      try {
        const cacheKey = this.taskSwitchChecked ? "org" : "user"

        if (allowCache && this.pastDueCache[cacheKey]) {
          this.pastDueData = this.pastDueCache[cacheKey]
          return
        }

        const res = this.taskSwitchChecked
          ? await apiService.GetInternalTasksByOrgId(this.azureId, this.accessToken)
          : await apiService.GetInternalTasks(this.azureId, this.accessToken)

        const raw = res?.data?.data?.data ?? res?.data?.data ?? []

        const computed = raw
          .map((item) => ({
            ...item,
            followupdate: this.normalizeDate(item.followupdate),
            created: this.normalizeDate(item.created)
          }))
          .filter((item) => isPastDue(item.followupdate) && item.status !== "Completed")

        this.pastDueData = computed
        this.pastDueCache[cacheKey] = computed
      } catch (err) {
        this.pastDueData = []
        this.setRowError(key, err)
      } finally {
        this.setRowLoading(key, false)
      }
    },

    openNewApps() {
      if (this.rowState.newApps.loading || this.rowState.newApps.error) return
      this.$emit("open-grid", { title: "New Applications", data: this.newAppsData, columns: this.newAppsColumns })
    },

    openExpiringLocks() {
      if (this.rowState.expiringLocks.loading || this.rowState.expiringLocks.error) return
      this.$emit("open-grid", { title: "Expiring Locks", data: this.expiringLocksActive, columns: this.expiringLocksColumns })
    },

   openPastDue() {
  if (this.rowState.pastDue.loading || this.rowState.pastDue.error) return

  const grouped = this._groupPastDueByLoan(this.pastDueData)

  this.$emit("open-grid", {
    title: "Past Due",
    dialogType: "internalTaskGrouped",
    data: grouped,
    columns: this.internalTaskColumns
  })
},

openPendingLLTasks() {
  if (this.rowState.pendingLLTasks.loading || this.rowState.pendingLLTasks.error) return

  const grouped = this._groupPendingLLByLoan(this.pendingLLTasksData)

  this.$emit("open-grid", {
    title: "Pending LL (Tasks)",
    dialogType: "borrowerTaskGrouped",
    data: grouped,
    columns: this.openTaskColumns,
    sendNotiButton: true
  })
},


    openDisclosures() {
      if (this.rowState.pendingLOSignature.loading || this.rowState.pendingLOSignature.error) return
      this.$emit("open-disclosures")
    },

    openLoanLaunch() {
      this.$emit("open-loanlaunch")
    }
  },
  async mounted() {
    await this.fetchAll()
  },
  components: {
    LoanLaunchChatSummaryCard
  }
}
</script>

<style scoped src="@/assets/css/commandcenter/commandcenter.css"></style>

<style scoped>
.cc-action-row:disabled {
  opacity: 0.75;
  cursor: not-allowed;
}

.cc-action-inline {
  margin-left: 8px;
  font-size: 12px;
  opacity: 0.8;
}

.cc-action-inline--error {
  opacity: 1;
}
</style>
